/**
 * API Service for Configuration Management
 * Handles CRUD operations for heating plant configurations (silos, boilers, pilot mode)
 */

import apiClient from './index';

// Note: apiClient already has /predibiomass/api/v1 as baseURL, so we don't add it here

/**
 * Get HeatingPlant by config ID
 */
export async function getHeatingPlant(configId) {
  console.log('🔧 GET HeatingPlant for config:', configId);
  const response = await apiClient.get('/heatingplants');
  // Trouver le HeatingPlant qui correspond à ce config_id
  const plant = response.data.find((p) => p.layout_id === configId);
  console.log('📦 HeatingPlant found:', plant);
  return plant;
}

/**
 * Get all Silos for a HeatingPlant
 */
export async function getSilos(plantId) {
  console.log('🏗️ GET Silos for plant:', plantId);
  const response = await apiClient.get(`/heatingplants/${plantId}/silos`);
  console.log('📦 Silos loaded:', response.data);
  return response.data;
}

/**
 * Get all Boilers for a HeatingPlant
 */
export async function getBoilers(plantId) {
  console.log('🔥 GET Boilers for plant:', plantId);
  const response = await apiClient.get(`/heatingplants/${plantId}/boilers`);
  console.log('📦 Boilers loaded:', response.data);
  return response.data;
}

/**
 * Transform a raw backend silo object into the frontend Vuex format.
 * Uses nullish coalescing so valid 0 values are preserved and unset fields become null.
 * @param {Object} s - Raw backend silo
 * @returns {Object} Frontend silo object
 */
export function transformSilo(s) {
  return {
    id: s.id,
    volumeTotal: s.size ?? null,
    niveauMin: s.limit_low ?? null,
    pciMoyen: s.wood_pci ?? null,
    densite: s.wood_d ?? null,
    volumeCamion: s.truck_vol ?? null,
    nbCamionsMax: s.truck_load_max ?? null,
    nbCamionsMin: s.truck_load_min ?? null,
  };
}

/**
 * Transform a raw backend boiler object into the frontend Vuex format.
 * Uses nullish coalescing so valid 0 values are preserved and unset fields become null.
 * @param {Object} b - Raw backend boiler
 * @param {number} index - Position index (used for default priority)
 * @returns {Object} Frontend boiler object
 */
export function transformBoiler(b, index) {
  return {
    id: b.id,
    name: `Chaudière ${index + 1}`,
    pNominale: b.power_rated ?? null,
    pMinimale: b.power_min ?? null,
    rendement: b.output ?? null,
    charge: b.load ?? null,
    priority: b.order ?? index + 1,
    siloId: b.silo_id ?? null,
  };
}

/**
 * Load existing configuration for an asset
 * @param {string} configId - Configuration ID (from Site.config_id)
 * @returns {Promise<Object>} Configuration data
 */
export async function loadConfiguration(configId) {
  try {
    console.log('📥 Loading configuration for config_id:', configId);

    // 1. Récupérer le HeatingPlant
    const plant = await getHeatingPlant(configId);

    if (!plant) {
      console.warn('⚠️ No HeatingPlant found for this configuration');
      return { silos: [], boilers: [], pilotMode: null };
    }

    // 2. Charger les Silos et Boilers en parallèle
    const [silos, boilers] = await Promise.all([getSilos(plant.layout_id), getBoilers(plant.layout_id)]);

    // 3. Transformer les données backend vers format Vuex
    const transformedSilos = silos.map((s) => transformSilo(s));

    const transformedBoilers = boilers.map((b, index) => transformBoiler(b, index));

    console.log('✅ Configuration loaded successfully');

    return {
      heatingPlantId: plant.layout_id,
      silos: transformedSilos,
      boilers: transformedBoilers,
      pilotMode: {
        mode: plant.control_sort || null,
        regulationMode: plant.control_sort || null,
        cogenActive: plant.is_coge ?? null,
        cogenPower: plant.power_coge ?? null,
        tagPrevision: plant.tag_predi_demand || null,
      },
    };
  } catch (error) {
    console.error('❌ Error loading configuration:', error);
    throw error;
  }
}

/**
 * Save complete configuration for an asset
 * @param {string} configId - Configuration ID
 * @param {string} plantId - Heating Plant ID
 * @param {Object} configuration - Configuration data (silos, boilers, pilotMode)
 * @returns {Promise<Object>} Saved configuration
 */
export async function saveConfiguration(configId, plantId, configuration) {
  try {
    console.log('💾 ========== SAVE CONFIGURATION START ==========');
    console.log('💾 Saving configuration for plant:', plantId);
    console.log('💾 Config ID:', configId);
    console.log('📋 Configuration data:', configuration);
    console.log('📋 Silos to save:', configuration.silos.length);
    console.log('📋 Boilers to save:', configuration.boilers.length);

    const results = {
      silos: [],
      boilers: [],
      heatingPlant: null,
    };

    // 0. S'assurer que le HeatingPlant existe AVANT de créer les silos
    // (les silos ont une FK vers HeatingPlant).
    // On vérifie d'abord l'existence : si le plant n'existe pas encore on le crée,
    // sinon on réutilise l'existant sans le recréer.
    let existingSilos = [];
    let existingBoilers = [];

    try {
      let existingPlant = await getHeatingPlant(configId);

      if (!existingPlant) {
        // Premier enregistrement : créer le HeatingPlant
        console.log('➕ No HeatingPlant found – creating one');
        try {
          await apiClient.post(`/configurations/${configId}/heatingplants`, {});
          existingPlant = await getHeatingPlant(configId);
          console.log('✅ HeatingPlant created');
        } catch (createError) {
          console.error('❌ Failed to create HeatingPlant:', createError?.response?.status, createError?.message);
        }
      } else {
        // Plant existant : charger ses silos et boilers pour la gestion des suppressions
        console.log('📊 HeatingPlant already exists, loading existing silos/boilers');
        existingSilos = await getSilos(existingPlant.layout_id);
        existingBoilers = await getBoilers(existingPlant.layout_id);
        console.log('📊 Existing silos:', existingSilos.length, 'boilers:', existingBoilers.length);
      }
    } catch (error) {
      console.log('⚠️ Error while checking HeatingPlant existence:', error.message);
    }

    // 1. Sauvegarder ou créer chaque Silo
    const siloIdsToKeep = [];

    const siloPromises = configuration.silos.map(async (silo) => {
      try {
        const siloData = {
          wood_pci: silo.pciMoyen,
          wood_d: silo.densite,
          limit_high: 100,
          limit_low: silo.niveauMin,
          size: silo.volumeTotal,
          truck_vol: silo.volumeCamion ?? null,
          truck_load_max: silo.nbCamionsMax ?? null,
          truck_load_min: silo.nbCamionsMin ?? null,
        };

        let savedSilo;
        if (silo.id && typeof silo.id === 'string' && silo.id.includes('-')) {
          // UUID existant - UPDATE
          console.log(`📝 Updating silo ${silo.id}`);
          const response = await apiClient.put(`/heatingplants/${plantId}/silos/${silo.id}`, siloData);
          savedSilo = response.data;
          siloIdsToKeep.push(silo.id);
        } else {
          // Nouveau silo - CREATE
          console.log('➕ Creating new silo');
          const response = await apiClient.post(`/heatingplants/${plantId}/silos`, siloData);
          savedSilo = response.data;
          siloIdsToKeep.push(savedSilo.id);
        }

        return { frontendId: silo.id, backendSilo: savedSilo };
      } catch (error) {
        console.error('❌ Error saving silo:', error);
        return null;
      }
    });

    const savedSilosMappings = await Promise.all(siloPromises);
    const validMappings = savedSilosMappings.filter((m) => m !== null);
    results.silos = validMappings.map((m) => m.backendSilo);

    // Build frontend silo ID -> backend silo ID mapping (for boiler association)
    const siloIdMap = {};
    validMappings.forEach((m) => {
      siloIdMap[m.frontendId] = m.backendSilo.id;
    });

    // 1b. Supprimer les silos qui ne sont plus dans la configuration
    const deletePromises = existingSilos
      .filter((existingSilo) => !siloIdsToKeep.includes(existingSilo.id))
      .map(async (existingSilo) => {
        try {
          console.log(`🗑️ ========== DELETING SILO ${existingSilo.id} ==========`);
          await apiClient.delete(`/heatingplants/${plantId}/silos/${existingSilo.id}`);
          console.log(`✅ Silo ${existingSilo.id} deleted successfully`);
        } catch (error) {
          console.error(`❌ Error deleting silo ${existingSilo.id}:`, error);
        }
      });

    console.log(`🗑️ Total silos to delete: ${deletePromises.length}`);
    await Promise.all(deletePromises);

    // 2. Sauvegarder ou créer chaque Chaudière
    // Note: Les boilers doivent être liées à un silo
    const firstSiloId = results.silos[0]?.id;

    if (firstSiloId) {
      const boilerIdsToKeep = [];

      const boilerPromises = configuration.boilers.map(async (boiler) => {
        // Resolve the target silo ID: use the boiler's selected silo (via mapping), fall back to first silo
        const targetSiloId = siloIdMap[boiler.siloId] || firstSiloId;

        try {
          const boilerData = {
            power_rated: boiler.pNominale,
            power_min: boiler.pMinimale,
            output: boiler.rendement,
            load: boiler.charge,
            order: boiler.priority,
            silo_id: targetSiloId,
          };

          let savedBoiler;
          if (boiler.id && typeof boiler.id === 'string' && boiler.id.includes('-')) {
            // UUID existant - UPDATE
            console.log(`📝 Updating boiler ${boiler.id}`);
            const response = await apiClient.put(`/heatingplants/${plantId}/silos/${targetSiloId}/boilers/${boiler.id}`, boilerData);
            savedBoiler = response.data;
            boilerIdsToKeep.push(boiler.id);
          } else {
            // Nouvelle chaudière - CREATE
            console.log('➕ Creating new boiler');
            const response = await apiClient.post(`/heatingplants/${plantId}/silos/${targetSiloId}/boilers`, boilerData);
            savedBoiler = response.data;
            boilerIdsToKeep.push(savedBoiler.id);
          }
          return savedBoiler;
        } catch (error) {
          console.error('❌ Error saving boiler:', error);
          return null;
        }
      });

      const savedBoilersResults = await Promise.all(boilerPromises);
      results.boilers = savedBoilersResults.filter((b) => b !== null);

      // 2b. Supprimer les chaudières qui ne sont plus dans la configuration
      const deleteBoilerPromises = existingBoilers
        .filter((existingBoiler) => !boilerIdsToKeep.includes(existingBoiler.id))
        .map(async (existingBoiler) => {
          try {
            console.log(`🗑️ ========== DELETING BOILER ${existingBoiler.id} ==========`);
            await apiClient.delete(`/heatingplants/${plantId}/silos/${existingBoiler.silo_id}/boilers/${existingBoiler.id}`);
            console.log(`✅ Boiler ${existingBoiler.id} deleted successfully`);
          } catch (error) {
            console.error(`❌ Error deleting boiler ${existingBoiler.id}:`, error);
          }
        });

      console.log(`🗑️ Total boilers to delete: ${deleteBoilerPromises.length}`);
      await Promise.all(deleteBoilerPromises);
    }

    console.log('💾 ========== SAVE CONFIGURATION END ==========');

    // 3. Mettre à jour le HeatingPlant avec les données de pilotage
    try {
      console.log('🔧 Updating HeatingPlant with pilot mode data');
      const plantData = {
        is_coge: configuration.pilotMode?.cogenActive ?? false,
        power_coge: configuration.pilotMode?.cogenPower ?? 0,
        control_sort: configuration.pilotMode?.regulationMode ?? configuration.pilotMode?.mode ?? null,
        silo_count: results.silos.length,
        boiler_total: results.boilers.length,
        tag_predi_demand: configuration.pilotMode?.tagPrevision || null,
      };

      const response = await apiClient.put(`/configurations/${configId}/heatingplants/${plantId}`, plantData);
      results.heatingPlant = response.data;
    } catch (error) {
      console.error('❌ Error updating heating plant:', error);
    }

    console.log('✅ Configuration saved successfully');
    console.log('📊 Results:', results);

    return {
      success: true,
      message: 'Configuration enregistrée avec succès',
      data: results,
    };
  } catch (error) {
    console.error('❌ Error saving configuration:', error);
    throw error;
  }
}

/**
 * Add a new silo to the heating plant
 * @param {string} plantId - Heating Plant ID
 * @param {Object} silo - Silo data (name, capacity, unit)
 * @returns {Promise<Object>} Created silo
 */
export async function createSilo(plantId, silo) {
  try {
    const response = await apiClient.post(`/heatingplants/${plantId}/silos`, silo);
    return response.data;
  } catch (error) {
    console.error('Error creating silo:', error);
    throw error;
  }
}

/**
 * Update an existing silo
 * @param {string} plantId - Heating Plant ID
 * @param {string} siloId - Silo ID
 * @param {Object} silo - Updated silo data
 * @returns {Promise<Object>} Updated silo
 */
export async function updateSilo(plantId, siloId, silo) {
  try {
    const response = await apiClient.put(`/heatingplants/${plantId}/silos/${siloId}`, silo);
    return response.data;
  } catch (error) {
    console.error('Error updating silo:', error);
    throw error;
  }
}

/**
 * Delete a silo
 * @param {string} plantId - Heating Plant ID
 * @param {string} siloId - Silo ID
 * @returns {Promise<void>}
 */
export async function deleteSilo(plantId, siloId) {
  try {
    await apiClient.delete(`/heatingplants/${plantId}/silos/${siloId}`);
  } catch (error) {
    console.error('Error deleting silo:', error);
    throw error;
  }
}

/**
 * Add a new boiler to the heating plant
 * @param {string} plantId - Heating Plant ID
 * @param {string} siloId - Silo ID (for association)
 * @param {Object} boiler - Boiler data
 * @returns {Promise<Object>} Created boiler
 */
export async function createBoiler(plantId, siloId, boiler) {
  try {
    const response = await apiClient.post(`/heatingplants/${plantId}/silos/${siloId}/boilers`, boiler);
    return response.data;
  } catch (error) {
    console.error('Error creating boiler:', error);
    throw error;
  }
}

/**
 * Update boiler priority order
 * @param {string} plantId - Heating Plant ID
 * @param {Array} boilers - Array of boilers with updated priorities
 * @returns {Promise<Array>} Updated boilers
 */
export async function updateBoilerPriorities(plantId, boilers) {
  try {
    const response = await apiClient.put(`/heatingplants/${plantId}/boilers/reorder`, { boilers });
    return response.data;
  } catch (error) {
    console.error('Error updating boiler priorities:', error);
    throw error;
  }
}

/**
 * Delete a boiler
 * @param {string} plantId - Heating Plant ID
 * @param {string} siloId - Silo ID
 * @param {string} boilerId - Boiler ID
 * @returns {Promise<void>}
 */
export async function deleteBoiler(plantId, siloId, boilerId) {
  try {
    await apiClient.delete(`/heatingplants/${plantId}/silos/${siloId}/boilers/${boilerId}`);
  } catch (error) {
    console.error('Error deleting boiler:', error);
    throw error;
  }
}
